// src/pages/AngebotsAnzeigePage.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { sendPlatformMessageData } from "../services/messagesDataService";

import {
  getCurrentAuthUser,
  subscribeToAuthChanges,
} from "../services/authDataService";

import {
  getOfferByIdData,
  getSavedOffersData,
  removeSavedOfferData,
  deleteOfferData,
  saveOfferForUserData,
} from "../services/profileDataService";

import "./AngebotsAnzeigePage.css";
import GateIcon from "../components/GateIcon";
import OfflineBanner from "../components/OfflineBanner";
import SpeechIcon from "../components/SpeechIcon";
import ContentActionMenu from "../components/ContentActionMenu";

function AngebotsAnzeigePage() {
  const navigate = useNavigate();
  const { angebotId } = useParams();
  const cleanAngebotId = decodeURIComponent(angebotId || "")
  .trim()
  .split(/\s+/)[0];

  const [currentUser, setCurrentUser] = useState(null);

  // 🔔 Offline-Status für Banner
  const [isOffline, setIsOffline] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);

  const [angebot, setAngebot] = useState(null);
  const [loadingOffer, setLoadingOffer] = useState(true);
  const [offerNotFound, setOfferNotFound] = useState(false);

  const [showLightbox, setShowLightbox] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [nachricht, setNachricht] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showLoginRequired, setShowLoginRequired] = useState(false);

  // ✅ Saved-Status + Toast
  const [isSaved, setIsSaved] = useState(false);
  const [saveToast, setSaveToast] = useState({ visible: false, text: "" });
  const toastTimerRef = useRef(null);

  // ✅ Admin / Rechte
  const [isAdmin, setIsAdmin] = useState(false);

  // ✅ EE Popups: Edit + Delete
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // kleines Feedback
  const [actionToast, setActionToast] = useState({ visible: false, text: "" });
  const actionToastTimerRef = useRef(null);

  const myUid = currentUser?.uid || null;
  const ownerId = angebot?.userId || null;
  const canManageOffer = !!myUid && (isAdmin === true || (!!ownerId && ownerId === myUid));

  const showActionToast = (text) => {
    if (actionToastTimerRef.current) clearTimeout(actionToastTimerRef.current);
    setActionToast({ visible: true, text });
    actionToastTimerRef.current = setTimeout(() => {
      setActionToast({ visible: false, text: "" });
    }, 2000);
  };

  // 🔍 Verbindung zu Firebase testen (für OfflineBanner)
  useEffect(() => {
    const checkConnection = async () => {
      try {
        await fetch("https://www.gstatic.com/firebasejs/9.6.1/firebase-app.js", {
          method: "HEAD",
          cache: "no-store",
        });
        setIsOffline(false);
      } catch (err) {
        console.warn("Keine Server-Verbindung möglich (AngebotsAnzeigePage):", err);
        setIsOffline(true);
      }
    };
    checkConnection();
  }, [retryAttempt]);

  const handleOfflineRetry = () => {
    setIsOffline(false);
    setTimeout(() => setRetryAttempt((prev) => prev + 1), 600);
  };

  // ✅ Auth-State konsistent laden
  useEffect(() => {
    let mounted = true;
    let unsub = null;

    const init = async () => {
      try {
        const user = await getCurrentAuthUser();

        if (mounted) {
          setCurrentUser(user || null);
        }

        unsub = await subscribeToAuthChanges((u) => {
          if (!mounted) return;
          setCurrentUser(u || null);
        });
      } catch (e) {
        console.warn("Auth init failed:", e);
      }
    };

    init();

    return () => {
      mounted = false;
      if (typeof unsub === "function") unsub();
    };
  }, []);

  // ✅ Admin-Claim laden (Custom Claim: admin=true)
  useEffect(() => {
    const loadAdminClaim = async () => {
      try {
        const user = currentUser;
        if (!user || typeof user.getIdTokenResult !== "function") {
          setIsAdmin(false);
          return;
        }

        const token = await user.getIdTokenResult(false);
        setIsAdmin(token?.claims?.admin === true);
      } catch {
        setIsAdmin(false);
      }
    };

    loadAdminClaim();
  }, [currentUser]);

  // 🔹 Angebot laden jetzt über Service
  useEffect(() => {
    let cancelled = false;

    const fetchAngebot = async () => {
      setLoadingOffer(true);
      setOfferNotFound(false);

      try {
        const data = await getOfferByIdData(cleanAngebotId);

        if (cancelled) return;

        if (data) {
          setAngebot(data);
          setOfferNotFound(false);
        } else {
          setAngebot(null);
          setOfferNotFound(true);
        }
      } catch (error) {
        console.error("Fehler beim Laden des Angebots:", error);
        setAngebot(null);
        setOfferNotFound(true);
      } finally {
        if (!cancelled) setLoadingOffer(false);
      }
    };

    fetchAngebot();

    return () => {
      cancelled = true;
    };
  }, [cleanAngebotId]);

  // ✅ Images stabil ableiten + Upload-Placeholder rausfiltern
  const images = useMemo(() => {
    if (!angebot) return [];

    const isBad = (src) =>
      !src || src === "/upload-placeholder.png" || src === "/camera-placeholder.png";

    if (Array.isArray(angebot.images) && angebot.images.length > 0) {
      return angebot.images.filter((src) => !isBad(src));
    }
    if (angebot.image && !isBad(angebot.image)) return [angebot.image];
    return [];
  }, [angebot]);

  // ✅ Preload Bilder
  useEffect(() => {
    if (!images || images.length === 0) return;
    images.forEach((src) => {
      if (!src) return;
      const img = new Image();
      img.src = src;
      if (img.decode) img.decode().catch(() => {});
    });
  }, [images]);

  // 🔹 ESC/Arrow Steuerung für Lightbox
  useEffect(() => {
    const handleKey = (e) => {
      if (!showLightbox) return;

      if (e.key === "Escape") setShowLightbox(false);
      if (e.key === "ArrowLeft" && images.length > 1) {
        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
      }
      if (e.key === "ArrowRight" && images.length > 1) {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [showLightbox, images.length]);

  // ✅ Kontaktwege steuern Anzeige
  const kontaktwege = useMemo(() => {
    const k = angebot?.kontaktwege;
    return Array.isArray(k) ? k : [];
  }, [angebot]);

  const showPhone = kontaktwege.includes("Telefon");
  const showEmail = kontaktwege.includes("E-Mail");
  const showPlatform = kontaktwege.includes("Plattform");

  // ✅ Hauptbild + Lightbox auf gefilterten Images aufbauen
  const fallbackImage = angebot?.imageUrl || angebot?.image || "/placeholder.png";

  const safeMainIndex =
    typeof angebot?.mainImageIndex === "number" &&
    angebot.mainImageIndex >= 0 &&
    angebot.mainImageIndex < images.length
      ? angebot.mainImageIndex
      : 0;

  const mainImageSrc = images[safeMainIndex] || images[0] || fallbackImage;

  const openLightbox = (index = 0) => {
    if (!images.length) return;
    const safeIndex = Math.max(0, Math.min(index, images.length - 1));
    setCurrentImageIndex(safeIndex);
    setShowLightbox(true);
  };

  const handleNextImage = () => {
    if (images.length <= 1) return;
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrevImage = () => {
    if (images.length <= 1) return;
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // 🔹 Anbieter-Daten
  const seller = angebot?.seller || {};
  const sellerName = seller.name || angebot?.anbieterName || "Anbieter";

  const rawStreet = (seller.street || angebot?.street || "").toString().trim();

  const removeHouseNumber = (street) => {
    if (!street) return "";
    return street.replace(/\s+\d+\w*.*$/, "").trim();
  };

  const showHouseNumber =
    seller.showHouseNumber !== undefined
      ? seller.showHouseNumber
      : angebot?.showExactLocation || false;

  const displayStreet = showHouseNumber ? rawStreet : removeHouseNumber(rawStreet);

  const plz = (seller.plz || angebot?.plz || "").toString().trim();
  const displayAdresse = (angebot?.adresse || `${displayStreet}${plz ? ", " + plz : ""}`).trim();

  const sellerPhone = seller.telefon || angebot?.contactTelephone || "—";
  const sellerEmail = seller.email || angebot?.contactEmail || "—";

  const handleNachrichtSenden = async () => {
    const text = nachricht.trim();
    if (!text) return;

    const currentUserNow = currentUser;
    if (!currentUserNow) {
      setShowLoginRequired(true);
      return;
    }

    const senderUid = currentUserNow.uid;
    const senderEmail = currentUserNow.email || "ohne-email@edens-ernte.local";

    const recipientUid = angebot?.userId || null;
    const recipientEmail = sellerEmail || "";

    if (!recipientUid) {
      setShowLoginRequired(true);
      return;
    }

    try {
      await sendPlatformMessageData({
  senderId: senderUid,
  recipientId: recipientUid,
  senderEmail,
  recipientEmail,
  senderName: currentUserNow.displayName || "",
  recipientName: sellerName || "Anbieter",
  text,
  contextType: "angebot",
  contextId: angebot?.id || cleanAngebotId,
});
    } catch (error) {
      console.error("Fehler beim Senden der Nachricht:", error);
    } finally {
      setNachricht("");
      setShowConfirmation(true);
    }
  };

  // ✅ Saved-Status initial laden
  useEffect(() => {
    const loadSavedState = async () => {
      if (!currentUser?.uid) {
        setIsSaved(false);
        return;
      }

      try {
        const saved = await getSavedOffersData(currentUser.uid);
        setIsSaved(saved.some((o) => String(o.id) === String(cleanAngebotId)));
      } catch (err) {
        console.warn("Saved-State laden fehlgeschlagen:", err);
        setIsSaved(false);
      }
    };

    loadSavedState();
  }, [cleanAngebotId, currentUser]);

  const showSaveToast = (text) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setSaveToast({ visible: true, text });
    toastTimerRef.current = setTimeout(() => {
      setSaveToast({ visible: false, text: "" });
    }, 1800);
  };

  const handleToggleSave = async () => {
  if (!cleanAngebotId || !angebot) return;

  const user = currentUser;
    if (!user?.uid) {
      setShowLoginRequired(true);
      return;
    }

    try {
      if (!isSaved) {
        await saveOfferForUserData(user.uid, cleanAngebotId);
        setIsSaved(true);
        showSaveToast("Angebot gespeichert");
      } else {
        await removeSavedOfferData(user.uid, cleanAngebotId);
        setIsSaved(false);
        showSaveToast("Angebot nicht mehr gespeichert");
      }
    } catch (err) {
      console.warn("Fehler beim Speichern/Entspeichern:", err);
      showSaveToast("Speichern nicht möglich (offline?)");
    }
  };

  // ✅ EDIT flow (EE Popup)
  const handleAskEdit = () => {
    if (!myUid) {
      setShowLoginRequired(true);
      return;
    }
    if (!canManageOffer) {
      showActionToast("Keine Berechtigung");
      return;
    }
    setShowEditConfirm(true);
  };

  const handleConfirmEdit = () => {
    setShowEditConfirm(false);
    navigate(`/angebot-erstellen?edit=${encodeURIComponent(String(cleanAngebotId))}`);
  };

  // ✅ DELETE flow (EE Popup)
  const handleAskDelete = () => {
    if (!myUid) {
      setShowLoginRequired(true);
      return;
    }
    if (!canManageOffer) {
      showActionToast("Keine Berechtigung");
      return;
    }
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!currentUser?.uid) {
      setShowLoginRequired(true);
      return;
    }

    try {
      await deleteOfferData(currentUser.uid, cleanAngebotId);
      setShowDeleteConfirm(false);
      showActionToast("Angebot gelöscht");
      setTimeout(() => navigate("/browse"), 400);
    } catch (e) {
      console.warn("Delete fehlgeschlagen:", e);
      setShowDeleteConfirm(false);
      showActionToast("Löschen nicht möglich (offline?)");
    }
  };

  if (loadingOffer) return <p className="ladeanzeige">Angebot wird geladen...</p>;

  if (offerNotFound) {
    return (
      <div className="AngebotsAnzeigePage">
        {isOffline && <OfflineBanner onRetry={handleOfflineRetry} />}

        <img
          src="/logo-edens-ernte-transparent.png"
          alt="Logo"
          className="browse-logo no-tap-highlight"
          onClick={() => navigate("/")}
        />
        <GateIcon className="login-icon" />
        <p className="top-claim">Nachhaltigkeit – ganz einfach!</p>

        <div className="angebot-anzeige-container">
        
{actionToast.visible && (
  <div className="mainbox-toast">
    {actionToast.text}
  </div>
)}
          <h2>Angebot nicht gefunden</h2>
          <p>Dieses Angebot existiert nicht (mehr) oder du hast keinen Zugriff.</p>
          <div className="modal-buttons" style={{ marginTop: 16 }}>
            <button className="green-button" onClick={() => navigate("/browse")}>
              Zurück zur Übersicht
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!angebot) return <p className="ladeanzeige">Angebot wird geladen...</p>;

  return (
    <div className="AngebotsAnzeigePage">
      {isOffline && <OfflineBanner onRetry={handleOfflineRetry} />}

      <img
        src="/logo-edens-ernte-transparent.png"
        alt="Logo"
        className="browse-logo no-tap-highlight"
        onClick={() => navigate("/")}
      />
      <GateIcon className="login-icon" />
      <SpeechIcon className="speech-icon no-tap-highlight" />
      <img
        src="/rezept-icon.png"
        alt="Rezepthelfer Icon"
        className="rezept-icon no-tap-highlight"
        onClick={() => navigate("/rezepte")}
      />
      <p className="top-claim">Nachhaltigkeit – ganz einfach!</p>

      <div className="angebot-anzeige-container">
  {saveToast.visible && (
    <div className="mainbox-toast">
      {saveToast.text}
    </div>
  )}

  {actionToast.visible && (
    <div className="mainbox-toast">
      {actionToast.text}
    </div>
  )}

  <div className="bild-wrapper">
          <p className="kategorie">{angebot.category}</p>

          <div className="angebot-action-cluster">
  <button
    type="button"
    className={`angebot-save-heart ${isSaved ? "saved" : ""}`}
    onClick={(e) => {
      e.stopPropagation();
      handleToggleSave();
    }}
    title={isSaved ? "Aus gespeicherten entfernen" : "Angebot speichern"}
  >
    {isSaved ? "❤️" : "🤍"}
  </button>

  {canManageOffer && (
    <>
      <button
        type="button"
        className="angebot-edit-icon"
        onClick={(e) => {
          e.stopPropagation();
          handleAskEdit();
        }}
        title="Angebot bearbeiten"
      >
        ✏️
      </button>

      <button
        type="button"
        className="angebot-delete-icon"
        onClick={(e) => {
          e.stopPropagation();
          handleAskDelete();
        }}
        title="Angebot löschen"
      >
        🗑️
      </button>
    </>
  )}

  <ContentActionMenu
  contentType="offer"
  contentId={angebot?.id || cleanAngebotId}
  ownerId={angebot?.userId || null}
  ownerName={sellerName}
  contentTitle={angebot?.title || "Angebot bei Edens Ernte"}
  contentText={
    angebot?.description ||
    angebot?.beschreibung ||
    angebot?.content ||
    angebot?.text ||
    ""
  }
  currentUser={currentUser}
  shareLabel="Angebot teilen"
  reportLabel="Angebot melden"
  blockLabel="Anbieter blockieren"
  shareTitle={angebot?.title || "Angebot bei Edens Ernte"}
  shareText={
    angebot?.description ||
    angebot?.beschreibung ||
    angebot?.content ||
    angebot?.text ||
    ""
  }
  shareUrl={`/angebot/${cleanAngebotId}`}
  onLoginRequired={() => setShowLoginRequired(true)}
  onActionToast={showActionToast}
/>
</div>

          <img
            src={mainImageSrc}
            alt="Hauptbild"
            className="hauptbild"
            loading="eager"
            decoding="async"
            onClick={() => openLightbox(safeMainIndex)}
          />

    
        </div>

        <h2>{angebot.title}</h2>
        <hr />

        <div className="beschreibung-block">
          <span className="info-label">Beschreibung:</span>
          <span className="info-value">{angebot.description || angebot.content}</span>
        </div>

        <hr />

        <div className="anbieter-info-grid">
          <div className="info-row">
            <span className="info-label">Anbieter:</span>
            <span className="info-value">{sellerName}</span>
          </div>

          <div className="info-row">
            <span className="info-label">Adresse:</span>
            <span className="info-value">{displayAdresse}</span>
          </div>

          {showPhone && (
            <div className="info-row">
              <span className="info-label">Telefon:</span>
              <span className="info-value">{sellerPhone || "—"}</span>
            </div>
          )}

          {showEmail && (
            <div className="info-row">
              <span className="info-label">E-Mail:</span>
              <span className="info-value">{sellerEmail || "—"}</span>
            </div>
          )}
        </div>

        {showPlatform && (
          <div className="nachrichten-box">
            <textarea
              placeholder="Nachricht schreiben..."
              value={nachricht}
              onChange={(e) => setNachricht(e.target.value)}
            />
            <button onClick={handleNachrichtSenden}>Nachricht senden</button>
          </div>
        )}

        {showLightbox && images.length > 0 && (
          <div className="lightbox-overlay" onClick={() => setShowLightbox(false)}>
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <img
                src={images[currentImageIndex] || mainImageSrc}
                alt="Vorschau"
                className="lightbox-img"
                loading="eager"
                decoding="async"
              />

              {images.length > 1 ? (
                <div className="lightbox-button-row">
                  <button
                    className="beitrags-lightbox-button green"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrevImage();
                    }}
                  >
                    ‹ Zurück
                  </button>
                  <button
                    className="beitrags-lightbox-button red"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowLightbox(false);
                    }}
                  >
                    ✖ Schließen
                  </button>
                  <button
                    className="beitrags-lightbox-button green"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNextImage();
                    }}
                  >
                    Weiter ›
                  </button>
                </div>
              ) : (
                <div className="lightbox-button-row">
                  <button
                    className="beitrags-lightbox-button red"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowLightbox(false);
                    }}
                  >
                    ✖ Schließen
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {showConfirmation && (
          <div className="modal-overlay" onClick={() => setShowConfirmation(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Nachricht gesendet</h3>
              <p>Deine Nachricht wurde an den Anbieter gesendet.</p>
              <div className="modal-buttons">
                <button className="green-button" onClick={() => setShowConfirmation(false)}>
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {showLoginRequired && (
          <div className="modal-overlay" onClick={() => setShowLoginRequired(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Login erforderlich</h3>
              <p>Um diese Funktion zu nutzen, musst du eingeloggt sein.</p>
              <div className="modal-buttons">
                <button className="green-button" onClick={() => navigate("/login")}>
                  Zum Login
                </button>
                <button className="cancel-btn" onClick={() => setShowLoginRequired(false)}>
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}

        {showEditConfirm && (
          <div className="modal-overlay" onClick={() => setShowEditConfirm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Möchtest du das Angebot wirklich bearbeiten?</h3>
              <p>Du wirst zur Bearbeitungsseite weitergeleitet.</p>
              <div className="modal-buttons">
                <button className="green-button" onClick={handleConfirmEdit}>
                  Ja
                </button>
                <button className="cancel-btn" onClick={() => setShowEditConfirm(false)}>
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Möchtest du das Angebot wirklich löschen?</h3>
              <p>Diese Aktion kann nicht rückgängig gemacht werden.</p>
              <div className="modal-buttons">
                <button className="delete-btn" onClick={handleConfirmDelete}>
                  Löschen
                </button>
                <button className="cancel-btn" onClick={() => setShowDeleteConfirm(false)}>
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AngebotsAnzeigePage;